<template>
  <div class="date_range_picker" @click.stop>
    <div v-if="timezone" class="date_picker_timezone">
      <span>{{ $t('timezone.dashboard.info') }}</span>
      <span class="value">{{timezoneName}}</span>
    </div>
    <div class="date_range_picker_input--wrap">
      <span class="date_range_picker_input">
        <i class="icon icon--calendar-input"></i><input type="text" :value="startDateStr" readonly></span>
      <span> ~ </span>
      <span class="date_range_picker_input">
        <i class="icon icon--calendar-input"></i><input type="text" :value="endDateStr" readonly></span>
    </div>
    <div class="calendar clearfix">
      <month-calendar v-for="index in 2" :key="index"
        :class="index === 1 ? 'left' : 'right'"
        :uiType="index === 1 ? 'left' : 'right'"
        :monthStr="tempMonthStr"
        :minDate="minDate"
        :maxDate="computedMaxDate"
        :dateRange="tempDateRange"
        :initialDateRange="dateRange"
        :previewEndDate="previewEndDate"
        :timezone="timezone"
        :timezoneName="timezoneName"
        :onlyEnd="onlyEnd"
        :model="model"
        :bDataState="bDataState"
        @onMonthAdd="onMonthAdd"
        @onDateClick="onDateClick"
        @onRangePreviewChange="onRangePreviewChange"/>
    </div>
    <div class="date_picker-time-select" v-if="onTimeSelect">
      <TimeSelect :timezoneName="timezoneName"
                  :tempDateRange="tempDateRange"
                  :dateRange="dateRange"
                  :isEditMode="isEditMode"
                  :bDataState="bDataState"
                  :isPlusPlan="isPlusPlan"
                  :model="model"
                  @onTimeChange="onTimeChange"/>
    </div>
    <div class="clearfix">
      <p v-if="pickerType === 'reportPeriod' && !useTimes" class="notice">
        <span v-if="maxDays <= 31" v-html="$t('heatmap.datePeriodNotice', {maxDays: maxDays, periodMonth: periodMonth})"></span>
        <span v-else v-html="$t('heatmap.datePeriodNoticeExpand')"></span>
      </p>
      <p class="action" :class="{ab: useTimes}">
        <ui-button @click="onCancelClick">
          {{ $t('common.cancel') }}
        </ui-button>
        <ui-button primary
          :disabled="!canApply"
          @click="onConfirmClick">
          {{ $t('common.apply') }}
        </ui-button>
      </p>
    </div>
  </div>
</template>

<script>
import { format, parse, addMonths, isBefore, addDays, isSameDay } from 'date-fns'
import { getLocaleDateStr, getOffset } from '@/libs/utils'
import MonthCalendar from './MonthCalendar'
import UiButton from '@/components/ui/UiButton'
import { mapState } from 'vuex'
import TimeSelect from '@/components/date-picker/TimeSelect.vue'

export default {
  name: 'DateRangePicker',
  components: {
    TimeSelect,
    MonthCalendar,
    UiButton
  },
  props: {
    pickerType: {
      type: String,
      default: 'reportPeriod'
    },
    timezoneName: {
      type: String,
      required: false
    },
    minDate: {
      type: Date,
      required: false
    },
    maxDate: {
      type: Date,
      required: false
    },
    dateRange: {
      type: Object,
      default () {
        return {
          startDate: null,
          endDate: null
        }
      }
    },
    monthStr: {
      type: String,
      required: false
    },
    maxDays: {
      type: Number,
      default: 0
    },
    useTimes: {
      type: Boolean,
      default: null
    },
    onlyEnd: {
      type: Boolean,
      default: false
    },
    isSameDate: {
      type: Boolean,
      default: false
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    model: {
      type: Object,
      required: false
    },
    bDataState: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      tempMonthStr: null,
      tempDateRange: null,
      previewEndDate: null,
      startTime: null,
      endTime: null,
      periodMonth: 1
    }
  },
  watch: {
    maxDays () {
      this.calculatePeriodMonth()
    }
  },
  computed: {
    ...mapState([
      'applyData',
      'planData'
    ]),
    timezone () {
      return getOffset(this.timezoneName)
    },
    startDateStr () {
      if (this.tempDateRange.startDate) {
        return getLocaleDateStr(this.tempDateRange.startDate)
      } else {
        return this.$t('common.placeHolder.periodStart')
      }
    },
    endDateStr () {
      if (this.tempDateRange.endDate) {
        return getLocaleDateStr(this.tempDateRange.endDate)
      } else {
        return this.$t('common.placeHolder.periodEnd')
      }
    },
    canApply () {
      return this.tempDateRange.startDate && this.tempDateRange.endDate
    },
    computedMaxDate () {
      if (this.maxDays && this.tempDateRange.startDate && !this.tempDateRange.endDate) {
        const newEndDate = addDays(this.tempDateRange.startDate, this.maxDays - 1)

        if (isBefore(newEndDate, this.maxDate)) {
          return newEndDate
        }
      }
      return this.maxDate
    },
    onTimeSelect () {
      return isSameDay(this.tempDateRange.startDate, this.tempDateRange.endDate) && this.useTimes
    },
    isPlusPlan () {
      return this.planData.planNumber > 4000
    }
  },
  created () {
    this.calculatePeriodMonth()
    if (this.monthStr) {
      this.tempMonthStr = this.monthStr
    } else {
      const range = this.dateRange

      if (range && range.startDate && range.endDate) {
        this.tempMonthStr = format(range.endDate, 'YYYY-MM')
      } else {
        this.tempMonthStr = format(new Date(), 'YYYY-MM')
      }
    }
    this.setTempDateRange()

    this.closeFn = () => {
      this.onCancelClick()
    }
    document.body.addEventListener('click', this.closeFn, false)
  },
  destroyed () {
    document.body.removeEventListener('click', this.closeFn)
  },
  methods: {
    calculatePeriodMonth () {
      if (this.maxDays > 31) {
        this.periodMonth = Math.round(this.maxDays / 31)
      }
    },
    setTempDateRange () {
      this.tempDateRange = { ...this.dateRange }
    },
    onMonthAdd (months) {
      let monthDate = parse(this.tempMonthStr)
      monthDate = addMonths(monthDate, months)
      this.tempMonthStr = format(monthDate, 'YYYY-MM-DD')
    },
    onRangePreviewChange (type, date) {
      this.previewEndDate = date
    },
    onDateClick (type, date) {
      const range = this.tempDateRange

      if ((!range.startDate && !this.onlyEnd) || (range.endDate && !this.onlyEnd) || (!range.endDate && isBefore(date, range.startDate) && !this.onlyEnd)) {
        // range with startDate only
        range.startDate = date
        range.endDate = null
        this.previewEndDate = null
      } else {
        // range with startDate and endDate
        if (!range.startDate) {
          range.startDate = date
        } else {
          range.endDate = date
          this.previewEndDate = null
        }
      }
    },
    onCancelClick () {
      this.$emit('onRangeCancel')
    },
    onConfirmClick () {
      const range = this.tempDateRange

      if (!range.startDate || !range.endDate) {
        return
      }
      if (isSameDay(this.tempDateRange.startDate, this.tempDateRange.endDate)) {
        this.$emit('onRangeChange', {
          range: this.tempDateRange,
          monthStr: this.tempMonthStr,
          startTime: this.startTime,
          endTime: this.endTime
        })
      } else {
        this.$emit('onRangeChange', {
          range: this.tempDateRange,
          monthStr: this.tempMonthStr
        })
      }
    },
    onTimeChange (hour, minute) {
      this.startTime = hour
      this.endTime = minute
    }
  }
}
</script>

<style lang="scss" scoped>
.notice {
  font-weight: 400;
}
</style>
